import { db } from "@/firebase";
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import { auth } from "@/firebase";
import type { SessionBookingData } from "@/types/availability";
import { availabilityService } from "@/services/availabilityService";
import {
  sendSessionRescheduledEmail,
  sendSessionCancelledEmail,
  sendSessionBookedEmail,
  formatSessionDataForEmail,
} from "@/services/sessionEmailService";
import { parseTimeToMilitary, addHoursToTimeString } from "@/utils/dateUtils";
import { logDebug, logError, logWarn } from "@/utils/secureLogger";
import { verifyAgeForSession } from "@/utils/ageVerification";

export interface SessionData {
  studentId: string;
  tutorId: string;
  subject: string;
  mode: string;
  notes: string;
  startAt: Timestamp;
  endAt: Timestamp;
  sessionStatus: "pending" | "confirmed" | "in-progress" | "completed" | "cancelled";
  status: "active" | "archived";
  available: boolean; // Track if this time slot is available for booking
  location?: string;
  meetingLink?: string;
  cancellationReason?: string;
  archivedAt?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  // Reschedule tracking
  rescheduleReason?: string; // Latest reschedule reason
  // Pre-test and Post-test linking
  preTestId?: string; // ID of the pre-test taken before this session
  postTestId?: string; // ID of the post-test taken after this session
}

class SessionService {
  private sessionsCollection = collection(db, "sessions");

  /**
   * Check if a session can be rescheduled.
   * RULE: Only confirmed sessions have 12-hour restriction. Pending sessions can always be rescheduled.
   */
  canRescheduleSession(
    sessionData: SessionData,
    userType: "student" | "tutor"
  ): {
    canReschedule: boolean;
    reason?: string;
  } {
    // Check if session is in a reschedule-able state
    if (sessionData.sessionStatus === "completed") {
      return {
        canReschedule: false,
        reason: "Cannot reschedule a completed session",
      };
    }

    if (sessionData.sessionStatus === "cancelled") {
      return {
        canReschedule: false,
        reason: "Cannot reschedule a cancelled session",
      };
    }

    if (sessionData.sessionStatus === "in-progress") {
      return {
        canReschedule: false,
        reason: "Cannot reschedule a session that is in progress",
      };
    }

    const sessionStart = sessionData.startAt.toDate();
    const now = new Date();
    const hoursUntilSession = (sessionStart.getTime() - now.getTime()) / (1000 * 60 * 60);

    // If session has already started, cannot reschedule
    if (hoursUntilSession < 0) {
      return {
        canReschedule: false,
        reason: "Cannot reschedule a session that has already started",
      };
    }

    // Pending sessions can be rescheduled at any time (no 12-hour restriction)
    if (sessionData.sessionStatus === "pending") {
      return {
        canReschedule: true,
      };
    }

    // Confirmed sessions: Check 12-hour restriction
    if (sessionData.sessionStatus === "confirmed") {
      if (hoursUntilSession > 0 && hoursUntilSession <= 12) {
        return {
          canReschedule: false,
          reason: "Cannot reschedule less than 12 hours before the session",
        };
      }
    }

    return {
      canReschedule: true,
    };
  }

  /**
   * Create a new tutoring session
   */
  async createSession(sessionData: SessionBookingData): Promise<string> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("User must be authenticated to book a session");
      }

      // Check rate limiting before creating session
      await this._checkBookingRateLimit(currentUser.uid);

      // Check age verification for face-to-face sessions
      await this._checkAgeVerification(currentUser.uid, sessionData.mode);

      // Check tutor clearance for face-to-face sessions
      await this._checkTutorClearance(sessionData.tutorId, sessionData.mode);

      // Parse the date and time from the dateTime object
      const { date, timeSlot } = sessionData.dateTime;

      // Create start datetime
      const startDate = new Date(date.year, date.month, date.date);

      // Parse the time slot (e.g., "2:00 PM" or "2:00 PM - 4:00 PM")
      let startTime: string;
      let endTime: string;

      if (timeSlot.isAvailabilitySlot && timeSlot.startTime && timeSlot.endTime) {
        // Use the availability slot times
        startTime = timeSlot.startTime; // e.g., "14:00"
        endTime = timeSlot.endTime; // e.g., "16:00"
      } else {
        // Parse from display time (fallback)
        const timeStr = timeSlot.time; // e.g., "2:00 PM"
        startTime = parseTimeToMilitary(timeStr);
        // Default to 1-hour session if no end time
        endTime = addHoursToTimeString(startTime, 1);
      }

      // Create start and end timestamps
      const [startHour, startMin] = startTime.split(":").map(Number);
      const [endHour, endMin] = endTime.split(":").map(Number);

      const startAt = new Date(startDate);
      startAt.setHours(startHour, startMin, 0, 0);

      const endAt = new Date(startDate);
      endAt.setHours(endHour, endMin, 0, 0);

      // Check for overlapping sessions before creating
      await this._checkSessionOverlap(currentUser.uid, startAt, endAt);

      const now = Timestamp.now();

      // Build session object, only including defined fields
      const session: any = {
        studentId: currentUser.uid,
        tutorId: sessionData.tutorId,
        subject: sessionData.subject,
        mode: sessionData.mode,
        notes: sessionData.learningGoals,
        startAt: Timestamp.fromDate(startAt),
        endAt: Timestamp.fromDate(endAt),
        sessionStatus: "pending",
        status: "active",
        available: false, // Set to false when session is booked
        cancellationReason: null,
        archivedAt: null,
        createdAt: now,
        updatedAt: now,
        rescheduleReason: null,
      };

      // Only add location/meetingLink if they're relevant to avoid undefined values
      if (sessionData.mode === "face-to-face") {
        session.location = sessionData.location || "To be determined";
      }

      if (sessionData.mode === "online") {
        session.meetingLink = "To be provided";
      }

      // Add materials if provided
      if (sessionData.materials && sessionData.materials.length > 0) {
        session.materials = sessionData.materials;
      }

      const docRef = await addDoc(this.sessionsCollection, session);

      // Mark the slot as unavailable in tutor's availability
      await availabilityService.markSlotAsUnavailable(
        sessionData.tutorId,
        startDate,
        startTime,
        endTime,
        docRef.id
      );

      // Force clear the tutor's availability cache to ensure immediate updates
      availabilityService.clearTutorCache(sessionData.tutorId);

      // Force refresh availability data to ensure UI reflects the booking
      await availabilityService.forceRefreshTutorAvailability(sessionData.tutorId);

      // Send email notification to tutor about the new booking
      try {
        // Get tutor and student information from Firestore
        const tutorDoc = await getDoc(doc(db, "users", sessionData.tutorId));
        const studentDoc = await getDoc(doc(db, "users", currentUser.uid));

        if (tutorDoc.exists() && studentDoc.exists()) {
          const tutorData = tutorDoc.data();
          const studentData = studentDoc.data();

          // Format session date and time for email
          const sessionDate = startAt.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            timeZone: "Asia/Manila",
          });

          const sessionTime = `${startAt.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
            timeZone: "Asia/Manila",
          })} - ${endAt.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
            timeZone: "Asia/Manila",
          })}`;

          // Prepare email data
          const emailData = {
            tutorEmail: tutorData.email,
            tutorName: tutorData.displayName || tutorData.firstName || "Tutor",
            studentName: studentData.displayName || studentData.firstName || "Student",
            subject: sessionData.subject,
            sessionDate,
            sessionTime,
            sessionMode: sessionData.mode as "online" | "face-to-face",
            location: sessionData.location,
            learningGoals: sessionData.learningGoals || "Not specified",
          };

          // Send the booking notification email to tutor
          await sendSessionBookedEmail(emailData);
          logDebug("Session booking notification email sent to tutor");

          // Create audit log for session booking
          try {
            const { createAuditLog } = await import("@/services/auditLogService");

            await createAuditLog({
              action: "student_session_action",
              type: "system_event",
              details: `Student ${
                studentData.displayName || studentData.firstName || "Student"
              } booked ${sessionData.subject} session with ${
                tutorData.displayName || tutorData.firstName || "Tutor"
              } for ${sessionDate} at ${sessionTime}`,
              performedById: currentUser.uid,
              performedByName:
                studentData.displayName ||
                `${studentData.firstName || ""} ${studentData.lastName || ""}`.trim() ||
                studentData.email ||
                "Student",
              performedByEmail: studentData.email || "",
              affectedUserId: sessionData.tutorId,
              affectedUserName:
                tutorData.displayName ||
                `${tutorData.firstName || ""} ${tutorData.lastName || ""}`.trim() ||
                tutorData.email ||
                "Tutor",
              affectedUserEmail: tutorData.email || "",
              metadata: {
                sessionId: docRef.id,
                sessionSubject: sessionData.subject,
                sessionDate: sessionDate,
                sessionTime: sessionTime,
                sessionMode: sessionData.mode,
                actionType: "session_booking",
                learningGoals: sessionData.learningGoals,
              },
            });
            logDebug("Audit log created for session booking");
          } catch (auditError) {
            logError(
              "Failed to create audit log for session booking",
              auditError instanceof Error ? auditError : new Error(String(auditError))
            );
          }
        } else {
          logWarn("Could not send booking email: tutor or student data not found");
        }
      } catch (emailError) {
        // Log the error but don't fail the session creation
        logError(
          "Failed to send booking notification email",
          emailError instanceof Error ? emailError : new Error(String(emailError))
        );
      }

      return docRef.id;
    } catch (error) {
      logError("Error creating session", error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  }

  /**
   * Get sessions for a specific user
   */
  async getUserSessions(userId: string, userType: "student" | "tutor") {
    try {
      const fieldName = userType === "student" ? "studentId" : "tutorId";
      const q = query(
        this.sessionsCollection,
        where(fieldName, "==", userId),
        where("status", "==", "active"),
        orderBy("startAt", "desc")
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      logError(
        "Error fetching user sessions",
        error instanceof Error ? error : new Error(String(error))
      );
      throw error;
    }
  }

  /**
   * Update session status
   */
  async updateSessionStatus(sessionId: string, status: SessionData["sessionStatus"]) {
    try {
      const sessionRef = doc(this.sessionsCollection, sessionId);
      await updateDoc(sessionRef, {
        sessionStatus: status,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      logError(
        "Error updating session status",
        error instanceof Error ? error : new Error(String(error))
      );
      throw error;
    }
  }

  /**
   * Automatically update session status based on time (for monitoring service)
   */
  async updateSessionStatusAuto(
    sessionId: string,
    status: SessionData["sessionStatus"],
    reason?: string
  ) {
    try {
      const sessionRef = doc(this.sessionsCollection, sessionId);
      const updateData: any = {
        sessionStatus: status,
        updatedAt: Timestamp.now(),
      };

      // Add automatic transition metadata
      if (reason) {
        updateData.lastAutoTransition = reason;
        updateData.lastAutoTransitionAt = Timestamp.now();
      }

      await updateDoc(sessionRef, updateData);
    } catch (error) {
      logError(
        "Error automatically updating session status",
        error instanceof Error ? error : new Error(String(error))
      );
      throw error;
    }
  }

  /**
   * Reschedule a session
   */
  async rescheduleSession(
    sessionId: string,
    newDate: string,
    newTime: string,
    reason?: string,
    timeSlot?: any
  ): Promise<void> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("User must be authenticated to reschedule a session");
      }

      const sessionRef = doc(this.sessionsCollection, sessionId);
      const sessionDoc = await getDoc(sessionRef);

      if (!sessionDoc.exists()) {
        throw new Error("Session not found");
      }

      const sessionData = sessionDoc.data() as SessionData;

      // Determine if the current user is a student or tutor
      const currentUserId = currentUser.uid;
      const isStudentRescheduling = sessionData.studentId === currentUserId;
      const isTutorRescheduling = sessionData.tutorId === currentUserId;

      const userType = isStudentRescheduling ? "student" : "tutor";

      // Verify that the current user is authorized to reschedule this session
      if (!isStudentRescheduling && !isTutorRescheduling) {
        throw new Error("You are not authorized to reschedule this session");
      }

      // Check if session can be rescheduled
      const rescheduleCheck = this.canRescheduleSession(sessionData, userType);
      if (!rescheduleCheck.canReschedule) {
        throw new Error(rescheduleCheck.reason || "Session cannot be rescheduled");
      }

      // Parse the new date and time
      const [year, month, day] = newDate.split("-").map(Number);
      const [hours, minutes] = newTime.split(":").map(Number);

      // Create new start and end times
      const newStartDate = new Date(year, month - 1, day, hours, minutes, 0, 0);

      // Calculate duration - use time slot duration if available, otherwise use original session duration
      let durationMinutes: number;

      if (timeSlot && timeSlot.isAvailabilitySlot && timeSlot.duration) {
        // Use the duration from the selected availability slot
        durationMinutes = timeSlot.duration;
      } else {
        // Fall back to original session duration
        const originalStart = sessionData.startAt.toDate();
        const originalEnd = sessionData.endAt.toDate();
        const durationMs = originalEnd.getTime() - originalStart.getTime();
        durationMinutes = Math.round(durationMs / (1000 * 60));
      }

      const roundedDurationMs = durationMinutes * 1000 * 60;

      // Create end date by adding the duration
      const newEndDate = new Date(newStartDate.getTime() + roundedDurationMs);

      // Format original date and times for unblocking
      const originalStart = sessionData.startAt.toDate();
      const originalEnd = sessionData.endAt.toDate();
      const originalDateString = originalStart.toISOString().split("T")[0]; // YYYY-MM-DD
      const originalStartTime = `${originalStart
        .getHours()
        .toString()
        .padStart(2, "0")}:${originalStart.getMinutes().toString().padStart(2, "0")}`;
      const originalEndTime = `${originalEnd.getHours().toString().padStart(2, "0")}:${originalEnd
        .getMinutes()
        .toString()
        .padStart(2, "0")}`;

      // Format new date and times for blocking
      const newDateString = newDate; // Already in YYYY-MM-DD format
      const newStartTime = newTime; // Already in HH:MM format
      const newEndTime = `${newEndDate.getHours().toString().padStart(2, "0")}:${newEndDate
        .getMinutes()
        .toString()
        .padStart(2, "0")}`;

      // Unblock the original slot
      await availabilityService.markSlotAsAvailable(sessionData.tutorId, sessionId);

      // Prepare update data with reschedule tracking
      const updateData: any = {
        startAt: Timestamp.fromDate(newStartDate),
        endAt: Timestamp.fromDate(newEndDate),
        available: false, // Keep as false when rescheduled
        updatedAt: Timestamp.now(),
        rescheduleReason: reason || null,
      };

      // If a student is rescheduling, set status back to pending for tutor approval
      // If a tutor is rescheduling, keep the current status (usually 'confirmed')
      if (isStudentRescheduling) {
        updateData.sessionStatus = "pending";
      }

      // Update the session
      await updateDoc(sessionRef, updateData);

      // Block the new slot
      await availabilityService.markSlotAsUnavailable(
        sessionData.tutorId,
        newStartDate,
        newStartTime,
        newEndTime,
        sessionId
      );

      // Force clear the tutor's availability cache to ensure immediate updates
      availabilityService.clearTutorCache(sessionData.tutorId);

      // Force refresh availability data to ensure UI reflects the reschedule
      await availabilityService.forceRefreshTutorAvailability(sessionData.tutorId);

      // Get user data for notifications and audit logs
      let recipientEmail = "";
      let recipientName = "";
      let otherPartyName = "";
      let rescheduledBy: "student" | "tutor";

      if (isStudentRescheduling) {
        // Student rescheduled - notify tutor
        rescheduledBy = "student";
        const tutorDoc = await getDoc(doc(db, "users", sessionData.tutorId));
        if (tutorDoc.exists()) {
          const tutorData = tutorDoc.data();
          recipientEmail = tutorData.email;
          recipientName = `${tutorData.firstName} ${tutorData.lastName}`;
        }

        const studentDoc = await getDoc(doc(db, "users", sessionData.studentId));
        if (studentDoc.exists()) {
          const studentData = studentDoc.data();
          otherPartyName = `${studentData.firstName} ${studentData.lastName}`;
        }
      } else {
        // Tutor rescheduled - notify student
        rescheduledBy = "tutor";
        const studentDoc = await getDoc(doc(db, "users", sessionData.studentId));
        if (studentDoc.exists()) {
          const studentData = studentDoc.data();
          recipientEmail = studentData.email;
          recipientName = `${studentData.firstName} ${studentData.lastName}`;
        }

        const tutorDoc = await getDoc(doc(db, "users", sessionData.tutorId));
        if (tutorDoc.exists()) {
          const tutorData = tutorDoc.data();
          otherPartyName = `${tutorData.firstName} ${tutorData.lastName}`;
        }
      }

      // Create audit log for session reschedule (always create, regardless of email)
      try {
        const { createAuditLog } = await import("@/services/auditLogService");

        // Format dates for audit log
        const oldSessionDate = originalStart.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
          timeZone: "Asia/Manila",
        });

        const newSessionDate = newStartDate.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
          timeZone: "Asia/Manila",
        });

        const oldSessionTime = `${originalStart.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
          timeZone: "Asia/Manila",
        })} - ${originalEnd.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
          timeZone: "Asia/Manila",
        })}`;

        const newSessionTime = `${newStartDate.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
          timeZone: "Asia/Manila",
        })} - ${newEndDate.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
          timeZone: "Asia/Manila",
        })}`;

        await createAuditLog({
          action: isStudentRescheduling ? "student_session_action" : "tutor_session_action",
          type: "system_event",
          details: `${isStudentRescheduling ? "Student" : "Tutor"} ${otherPartyName} rescheduled ${
            sessionData.subject
          } session with ${recipientName} from ${oldSessionDate} at ${oldSessionTime} to ${newSessionDate} at ${newSessionTime}${
            reason ? ` - Reason: ${reason}` : ""
          }`,
          performedById: currentUser.uid,
          performedByName: otherPartyName,
          performedByEmail: currentUser.email || "",
          affectedUserId: isStudentRescheduling ? sessionData.tutorId : sessionData.studentId,
          affectedUserName: recipientName,
          affectedUserEmail: recipientEmail,
          metadata: {
            sessionId: sessionId,
            sessionSubject: sessionData.subject,
            oldSessionDate: oldSessionDate,
            oldSessionTime: oldSessionTime,
            newSessionDate: newSessionDate,
            newSessionTime: newSessionTime,
            sessionMode: sessionData.mode,
            actionType: "session_reschedule",
            reschedulingReason: reason || "No reason provided",
            rescheduledBy: rescheduledBy,
          },
        });
        logDebug("Audit log created for session reschedule");
      } catch (auditError) {
        logError(
          "Failed to create audit log for session reschedule",
          auditError instanceof Error ? auditError : new Error(String(auditError))
        );
      }

      // Send email notifications
      try {
        if (recipientEmail) {
          // Format dates for email
          const oldDate = originalStart.toISOString().split("T")[0];
          const oldTime = originalStartTime;

          const emailData = {
            recipientEmail: recipientEmail, // Use actual recipient email
            recipientName,
            otherPartyName,
            subject: sessionData.subject,
            oldSessionDate: oldDate,
            oldSessionTime: oldTime,
            newSessionDate: newDateString,
            newSessionTime: newStartTime,
            sessionMode: sessionData.mode as "online" | "face-to-face",
            location: sessionData.location,
            meetingLink: sessionData.meetingLink,
            rescheduledBy,
            reschedulingReason: reason || "No reason provided",
          };

          await sendSessionRescheduledEmail(emailData);
        }
      } catch (emailError) {
        logError(
          "Failed to send reschedule notification email",
          emailError instanceof Error ? emailError : new Error(String(emailError))
        );
        // Don't fail the whole reschedule process if email fails
      }
    } catch (error) {
      logError(
        "Error rescheduling session",
        error instanceof Error ? error : new Error(String(error))
      );
      throw error;
    }
  }

  /**
   * Cancel a session
   */
  async cancelSession(
    sessionId: string,
    cancellationReason: string,
    cancelledBy: string,
    cancelledAt?: Date
  ) {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("User must be authenticated to cancel a session");
      }

      const sessionRef = doc(this.sessionsCollection, sessionId);
      const sessionDoc = await getDoc(sessionRef);

      if (!sessionDoc.exists()) {
        throw new Error("Session not found");
      }

      const sessionData = sessionDoc.data();

      // Delete Google Calendar event if it exists (tutors are connected to Google Calendar)
      if (sessionData.calendarEventId) {
        try {
          const { deleteGoogleCalendarEvent } = await import("@/services/googleCalendarApi");

          // Try to delete the calendar event for the tutor
          const success = await deleteGoogleCalendarEvent(
            sessionData.tutorId,
            sessionData.calendarEventId
          );

          if (success) {
            logDebug("Calendar event deleted successfully", {
              sessionId,
              calendarEventId: sessionData.calendarEventId,
            });
          } else {
            logWarn("Calendar event deletion returned false", {
              sessionId,
              calendarEventId: sessionData.calendarEventId,
            });
          }
        } catch (calendarError) {
          logError(
            "Failed to delete Google Calendar event during cancellation",
            calendarError instanceof Error ? calendarError : new Error(String(calendarError))
          );
          // Don't fail the whole cancellation if calendar deletion fails
        }
      }

      // Update session status to cancelled
      await updateDoc(sessionRef, {
        sessionStatus: "cancelled",
        cancellationReason,
        cancelledBy: cancelledBy,
        cancelledAt: cancelledAt ? Timestamp.fromDate(cancelledAt) : Timestamp.now(),
        calendarEventId: null, // Clear the calendar event ID
        updatedAt: Timestamp.now(),
      });

      // Mark the slot as available again in tutor's availability
      await availabilityService.markSlotAsAvailable(sessionData.tutorId, sessionId);

      // Force clear the tutor's availability cache to ensure immediate updates
      availabilityService.clearTutorCache(sessionData.tutorId);

      // Force refresh availability data to ensure UI reflects the cancellation
      await availabilityService.forceRefreshTutorAvailability(sessionData.tutorId);

      // Send cancellation email notification
      try {
        // Get user data for email notifications (already imported at top)

        // Determine who cancelled and who should be notified
        const currentUserId = currentUser.uid;
        const isStudentCancelling = sessionData.studentId === currentUserId;
        const isTutorCancelling = sessionData.tutorId === currentUserId;

        let recipientEmail = "";
        let recipientName = "";
        let otherPartyName = "";
        let cancelledByType: "student" | "tutor";

        if (isStudentCancelling) {
          // Student cancelled - notify tutor
          cancelledByType = "student";
          const tutorDoc = await getDoc(doc(db, "users", sessionData.tutorId));
          if (tutorDoc.exists()) {
            const tutorData = tutorDoc.data();
            recipientEmail = tutorData.email;
            recipientName = `${tutorData.firstName} ${tutorData.lastName}`;
          }

          const studentDoc = await getDoc(doc(db, "users", sessionData.studentId));
          if (studentDoc.exists()) {
            const studentData = studentDoc.data();
            otherPartyName = `${studentData.firstName} ${studentData.lastName}`;
          }
        } else if (isTutorCancelling) {
          // Tutor cancelled - notify student
          cancelledByType = "tutor";
          const studentDoc = await getDoc(doc(db, "users", sessionData.studentId));
          if (studentDoc.exists()) {
            const studentData = studentDoc.data();
            recipientEmail = studentData.email;
            recipientName = `${studentData.firstName} ${studentData.lastName}`;
          }

          const tutorDoc = await getDoc(doc(db, "users", sessionData.tutorId));
          if (tutorDoc.exists()) {
            const tutorData = tutorDoc.data();
            otherPartyName = `${tutorData.firstName} ${tutorData.lastName}`;
          }
        } else {
          // Default to student as cancelled by if unclear
          cancelledByType = "student";
        }

        if (recipientEmail) {
          // Format session date and time for email (using same format as approval/decline emails)
          const sessionStart = sessionData.startAt.toDate();
          const sessionEnd = sessionData.endAt
            ? sessionData.endAt.toDate()
            : new Date(sessionStart.getTime() + 60 * 60 * 1000);

          // Use consistent formatting for Philippines timezone
          const sessionDate = sessionStart.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            timeZone: "Asia/Manila",
          });

          const sessionTime = `${sessionStart.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
            timeZone: "Asia/Manila",
          })} - ${sessionEnd.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
            timeZone: "Asia/Manila",
          })}`;

          const emailData = {
            recipientEmail: recipientEmail, // Use the recipientEmail that was already calculated
            recipientName,
            otherPartyName,
            subject: sessionData.subject,
            sessionDate,
            sessionTime,
            cancellationReason,
            cancelledBy: cancelledByType,
          };

          const emailResult = await sendSessionCancelledEmail(emailData);

          if (!emailResult) {
            logError(
              "Email sending failed - function returned false",
              new Error("Email send failed")
            );
          }

          // Create audit log for session cancellation
          try {
            const { createAuditLog } = await import("@/services/auditLogService");

            await createAuditLog({
              action: isStudentCancelling ? "student_session_action" : "tutor_session_action",
              type: "system_event",
              details: `${isStudentCancelling ? "Student" : "Tutor"} ${otherPartyName} cancelled ${
                sessionData.subject
              } session with ${recipientName} for ${sessionDate} at ${sessionTime} - Reason: ${cancellationReason}`,
              performedById: currentUser.uid,
              performedByName: otherPartyName,
              performedByEmail: currentUser.email || "",
              affectedUserId: isStudentCancelling ? sessionData.tutorId : sessionData.studentId,
              affectedUserName: recipientName,
              affectedUserEmail: recipientEmail,
              metadata: {
                sessionId: sessionId,
                sessionSubject: sessionData.subject,
                sessionDate: sessionDate,
                sessionTime: sessionTime,
                sessionMode: sessionData.mode,
                actionType: "session_cancellation",
                cancellationReason: cancellationReason,
                cancelledBy: cancelledByType,
              },
            });
            logDebug("Audit log created for session cancellation");
          } catch (auditError) {
            logError(
              "Failed to create audit log for session cancellation",
              auditError instanceof Error ? auditError : new Error(String(auditError))
            );
          }
        }
      } catch (emailError) {
        logError(
          "Failed to send cancellation notification email",
          emailError instanceof Error ? emailError : new Error(String(emailError))
        );
        // Don't fail the whole cancellation process if email fails
      }
    } catch (error) {
      logError(
        "Error cancelling session",
        error instanceof Error ? error : new Error(String(error))
      );
      throw error;
    }
  }

  /**
   * Public method to check booking rate limit for a student
   */
  async checkBookingRateLimit(studentId: string): Promise<void> {
    return this._checkBookingRateLimit(studentId);
  }

  /**
   * Check if user has exceeded booking rate limit (3 sessions per 15 minutes)
   */
  private async _checkBookingRateLimit(studentId: string): Promise<void> {
    try {
      // Calculate the time threshold (15 minutes ago)
      const fifteenMinutesAgo = new Date();
      fifteenMinutesAgo.setMinutes(fifteenMinutesAgo.getMinutes() - 15);

      // Query recent sessions for this student
      const recentSessionsQuery = query(
        this.sessionsCollection,
        where("studentId", "==", studentId),
        where("createdAt", ">=", Timestamp.fromDate(fifteenMinutesAgo))
      );

      const querySnapshot = await getDocs(recentSessionsQuery);

      // Filter to only count sessions actually created in the last 15 minutes
      // This prevents old sessions with corrupted timestamps from affecting rate limit
      const now = Date.now();
      const recentSessions = querySnapshot.docs
        .map((doc) => ({
          id: doc.id,
          createdAt: doc.data().createdAt?.toDate(),
        }))
        .filter((session) => {
          if (!session.createdAt) return false;
          const sessionTime = session.createdAt.getTime();
          // Only count if created in last 15 minutes AND not in the future
          return sessionTime >= fifteenMinutesAgo.getTime() && sessionTime <= now;
        })
        .sort((a, b) => a.createdAt!.getTime() - b.createdAt!.getTime());

      const recentBookingsCount = recentSessions.length;

      logDebug("Rate limit check", {
        studentId,
        recentBookingsCount,
        threshold: 3,
        window: "15 minutes",
      });

      // Check if limit exceeded
      if (recentBookingsCount >= 3) {
        // Calculate when user can book again based on the oldest session in the window
        const oldestSession = recentSessions[0];

        if (oldestSession && oldestSession.createdAt) {
          const nextAllowedTime = new Date(oldestSession.createdAt.getTime() + 15 * 60 * 1000);
          const minutesRemaining = Math.max(
            0,
            Math.ceil((nextAllowedTime.getTime() - now) / (60 * 1000))
          );

          // If minutesRemaining is 0 or negative, something is wrong with the timestamps
          // In this case, log the issue but don't block the booking
          if (minutesRemaining <= 0) {
            logWarn("Rate limit calculation returned invalid time", {
              oldestSessionCreatedAt: oldestSession.createdAt,
              nextAllowedTime,
              minutesRemaining,
              recentBookingsCount,
            });
            return; // Don't block booking if calculation is invalid
          }

          const errorMessage = `Booking limit exceeded. You can only book 3 sessions within a 15-minute period. Please wait ${minutesRemaining} more minute${
            minutesRemaining === 1 ? "" : "s"
          } before booking another session.`;
          throw new Error(errorMessage);
        }
      }
    } catch (error) {
      // If it's our rate limiting error, re-throw it
      if (
        error instanceof Error &&
        (error.message.includes("Booking limit exceeded") ||
          error.message.includes("You can only book 3 sessions"))
      ) {
        throw error;
      }

      // For other Firebase errors, log but don't block booking
      logWarn(
        "Error in rate limit check (not blocking)",
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }

  /**
   * Check if the new session overlaps with any existing active sessions
   */
  private async _checkSessionOverlap(
    studentId: string,
    newStartAt: Date,
    newEndAt: Date
  ): Promise<void> {
    try {
      // Query active sessions for this student
      const studentSessionsQuery = query(
        this.sessionsCollection,
        where("studentId", "==", studentId),
        where("status", "==", "active"),
        where("sessionStatus", "in", ["pending", "confirmed"])
      );

      const querySnapshot = await getDocs(studentSessionsQuery);

      // Check each session for overlap
      for (const docSnapshot of querySnapshot.docs) {
        const existingSession = docSnapshot.data();
        const existingStartAt = existingSession.startAt?.toDate();
        const existingEndAt = existingSession.endAt?.toDate();

        if (!existingStartAt || !existingEndAt) continue;

        // Check if sessions overlap
        // Sessions overlap if: newStart < existingEnd AND newEnd > existingStart
        const hasOverlap = newStartAt < existingEndAt && newEndAt > existingStartAt;

        if (hasOverlap) {
          // Format the existing session time for the error message
          const dateOptions: Intl.DateTimeFormatOptions = {
            month: "long",
            day: "numeric",
            year: "numeric",
          };
          const timeOptions: Intl.DateTimeFormatOptions = {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          };

          const formattedDate = existingStartAt.toLocaleDateString("en-US", dateOptions);
          const formattedStartTime = existingStartAt.toLocaleTimeString("en-US", timeOptions);
          const formattedEndTime = existingEndAt.toLocaleTimeString("en-US", timeOptions);
          const subject = existingSession.subject || "a session";

          const errorMessage = `You already have a session scheduled from ${formattedStartTime} to ${formattedEndTime} on ${formattedDate} (${subject}). Kindly choose another date and time.`;
          throw new Error(errorMessage);
        }
      }
    } catch (error) {
      // If it's our overlap error, re-throw it
      if (
        error instanceof Error &&
        error.message.includes("You already have a session scheduled")
      ) {
        throw error;
      }

      // For other Firebase errors, log but don't block booking
      logWarn(
        "Error in session overlap check (not blocking)",
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }

  /**
   * Check age verification for face-to-face sessions
   */
  private async _checkAgeVerification(studentId: string, sessionMode: string): Promise<void> {
    try {
      // Only check age for face-to-face sessions
      if (sessionMode !== "face-to-face") {
        logDebug("Age verification: Skipping for online session");
        return;
      }

      // Get user profile from Firestore
      const userDoc = await getDoc(doc(db, "users", studentId));

      if (!userDoc.exists()) {
        throw new Error("User profile not found. Please complete your profile setup.");
      }

      const userData = userDoc.data();
      const dateOfBirth = userData.dateOfBirth;

      if (!dateOfBirth) {
        throw new Error(
          "Date of birth is required in your profile to book face-to-face sessions. Please update your profile."
        );
      }

      // Verify age using the utility function
      const ageVerification = verifyAgeForSession(dateOfBirth, sessionMode);

      if (!ageVerification.isValid) {
        throw new Error(ageVerification.message);
      }

      logDebug("Age verification: Passed", {
        studentId,
        dateOfBirth,
        age: ageVerification.age,
        sessionMode,
      });
    } catch (error) {
      // Re-throw age verification errors
      if (
        error instanceof Error &&
        (error.message.includes("Date of birth") ||
          error.message.includes("must be 18 years") ||
          error.message.includes("profile not found"))
      ) {
        throw error;
      }

      // For other errors, log and throw a generic message
      logError("Age verification error", error);
      throw new Error(
        "Unable to verify age for face-to-face session. Please try again or contact support."
      );
    }
  }

  /**
   * Check tutor clearance for face-to-face sessions
   */
  private async _checkTutorClearance(tutorId: string, sessionMode: string): Promise<void> {
    try {
      // Only check clearance for face-to-face sessions
      if (sessionMode !== "face-to-face") {
        logDebug("Tutor clearance check: Skipping for online session");
        return;
      }

      // Get tutor profile from Firestore
      const tutorDoc = await getDoc(doc(db, "users", tutorId));

      if (!tutorDoc.exists()) {
        throw new Error("Tutor profile not found. Please try again or contact support.");
      }

      const tutorData = tutorDoc.data();

      // Check if tutor has submitted Barangay or NBI clearance
      const hasClearance = !!(
        tutorData.documents?.clearance?.url || tutorData.documents?.clearance
      );

      if (!hasClearance) {
        throw new Error(
          "This tutor has not submitted the required Barangay or NBI clearance and can only conduct online sessions. Please select an online session mode or choose a different tutor."
        );
      }

      logDebug("Tutor clearance check: Passed", {
        tutorId,
        hasClearance,
        sessionMode,
      });
    } catch (error) {
      // Re-throw clearance verification errors
      if (
        error instanceof Error &&
        (error.message.includes("clearance") || error.message.includes("Tutor profile not found"))
      ) {
        throw error;
      }

      // For other errors, log and throw a generic message
      logError("Tutor clearance verification error", error);
      throw new Error(
        "Unable to verify tutor clearance for face-to-face session. Please try again or contact support."
      );
    }
  }
}

// Lazy singleton instance to avoid circular dependency issues
let sessionServiceInstance: SessionService | null = null;

export function getSessionService(): SessionService {
  if (!sessionServiceInstance) {
    sessionServiceInstance = new SessionService();
  }
  return sessionServiceInstance;
}

// For backward compatibility, export a proxy that lazily initializes
export const sessionService = new Proxy({} as SessionService, {
  get(target, prop) {
    return getSessionService()[prop as keyof SessionService];
  },
});
export default SessionService;
